import type { FC, FormEvent, ReactNode } from 'react'
import type { CategoryWithCount } from '@/types'

import Link from 'next/link'

import { toast } from 'sonner'
import { useState } from 'react'

import {
    LoaderCircle,
    Pencil,
    Plus,
    Tags,
    Trash2
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { pluralize } from '@/lib/format'
import { useRefresh } from '@/hooks/use-refresh'
import { validateCategory } from '@/lib/validation'
import { ApiError, apiRequest, getErrorMessage } from '@/lib/api'
import { useSettings } from '@/components/generals/settings-provider'
import ColorSwatches from '@/components/inventory/color-swatches'
import EmptyState from '@/components/generals/empty-state'
import { CATEGORY_COLORS, LIMITS } from '@/constants/inventory'

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogMedia,
    AlertDialogTitle
} from '@/components/ui/alert-dialog'

interface CategoryManagerProps {
    categories: CategoryWithCount[]
    uncategorizedCount: number
}

interface CategoryRowProps {
    category: CategoryWithCount
    onDelete: (category: CategoryWithCount) => void
}

const CategoryRow: FC<CategoryRowProps> = ({ category, onDelete }): ReactNode => {
    const refresh = useRefresh()
    const { number } = useSettings()
    const [editing, setEditing] = useState(false)
    const [name, setName] = useState(category.name)
    const [color, setColor] = useState(category.color ?? CATEGORY_COLORS[0])
    const [error, setError] = useState<string | null>(null)
    const [saving, setSaving] = useState(false)
    const inputId = `category-${category.id}`

    const startEditing = () => {
        setName(category.name)
        setColor(category.color ?? CATEGORY_COLORS[0])
        setError(null)
        setEditing(true)
    }

    const handleSave = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const result = validateCategory({ name, color })

        if (!result.ok) {
            setError(result.errors.name ?? result.errors.color ?? 'Revisa los datos.')

            return
        }

        setSaving(true)
        setError(null)

        try {
            await apiRequest(`/api/categories/${category.id}`, { method: 'PUT', body: result.value })
            toast.success('Categoría actualizada')
            setEditing(false)
            await refresh()
        }
        catch (saveError) {
            setError(saveError instanceof ApiError ? saveError.fieldErrors.name ?? saveError.message : getErrorMessage(saveError))
        }
        finally {
            setSaving(false)
        }
    }

    if (editing) {
        return (
            <li className='bg-muted/40 px-4 py-4 sm:px-5'>
                <form onSubmit={handleSave} noValidate className='flex flex-col gap-3'>
                    <label htmlFor={inputId} className='text-[0.8rem] font-medium'>
                        Nombre de la categoría
                    </label>

                    <Input
                        id={inputId}
                        value={name}
                        maxLength={LIMITS.categoryName}
                        autoFocus
                        onChange={event => {
                            setName(event.target.value)
                            setError(null)
                        }}
                        onKeyDown={event => {
                            if (event.key === 'Escape') setEditing(false)
                        }}
                        aria-invalid={Boolean(error) || undefined}
                        aria-describedby={error ? `${inputId}-error` : undefined}
                        className='sm:max-w-sm'
                    />

                    {error && (
                        <p id={`${inputId}-error`} className='text-xs text-destructive'>
                            {error}
                        </p>
                    )}

                    <ColorSwatches value={color} onChange={setColor} label='Color de la categoría' />

                    <div className='flex flex-wrap gap-2'>
                        <Button type='submit' size='sm' disabled={saving}>
                            {saving && <LoaderCircle className='animate-spin' aria-hidden='true' />}
                            Guardar
                        </Button>

                        <Button type='button' size='sm' variant='ghost' onClick={() => setEditing(false)} disabled={saving}>
                            Cancelar
                        </Button>
                    </div>
                </form>
            </li>
        )
    }

    return (
        <li className='flex items-center gap-3 px-4 py-3 sm:px-5'>
            <span
                className='size-3 shrink-0 rounded-full'
                style={{ backgroundColor: category.color ?? 'var(--muted-foreground)' }}
                aria-hidden='true'
            />

            <div className='min-w-0 flex-1'>
                <p className='truncate text-sm font-medium'>
                    <span data-mdlfy-cms={`categories:${category.id}:name`}>{category.name}</span>
                </p>

                <p className='text-xs text-muted-foreground tabular-nums'>
                    {category.productCount ? (
                        <Link
                            href={{ pathname: '/inventario', query: { categoria: category.id } }}
                            className='hover:text-foreground hover:underline underline-offset-4'
                        >
                            {number(category.productCount)} {pluralize(category.productCount, 'producto', 'productos')}
                        </Link>
                    ) : 'Sin productos'}
                </p>
            </div>

            <div className='flex items-center gap-0.5'>
                <Button variant='ghost' size='icon-sm' onClick={startEditing} aria-label={`Editar la categoría ${category.name}`}>
                    <Pencil aria-hidden='true' />
                </Button>

                <Button
                    variant='ghost'
                    size='icon-sm'
                    onClick={() => onDelete(category)}
                    aria-label={`Eliminar la categoría ${category.name}`}
                    className='text-muted-foreground hover:bg-destructive/10 hover:text-destructive'
                >
                    <Trash2 aria-hidden='true' />
                </Button>
            </div>
        </li>
    )
}

const CategoryManager: FC<CategoryManagerProps> = ({ categories, uncategorizedCount }): ReactNode => {
    const refresh = useRefresh()
    const { number } = useSettings()
    const [name, setName] = useState('')
    const [color, setColor] = useState(CATEGORY_COLORS[categories.length % CATEGORY_COLORS.length])
    const [error, setError] = useState<string | null>(null)
    const [creating, setCreating] = useState(false)
    const [toDelete, setToDelete] = useState<CategoryWithCount | null>(null)
    const [deleteOpen, setDeleteOpen] = useState(false)
    const [deleting, setDeleting] = useState(false)

    const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const result = validateCategory({ name, color })

        if (!result.ok) {
            setError(result.errors.name ?? result.errors.color ?? 'Revisa los datos.')

            return
        }

        setCreating(true)
        setError(null)

        try {
            await apiRequest('/api/categories', { method: 'POST', body: result.value })
            toast.success(`Categoría «${result.value.name}» creada`)
            setName('')
            setColor(CATEGORY_COLORS[(categories.length + 1) % CATEGORY_COLORS.length])
            await refresh()
        }
        catch (createError) {
            setError(createError instanceof ApiError ? createError.fieldErrors.name ?? createError.message : getErrorMessage(createError))
        }
        finally {
            setCreating(false)
        }
    }

    const handleDelete = async () => {
        if (!toDelete) return

        setDeleting(true)

        try {
            await apiRequest(`/api/categories/${toDelete.id}`, { method: 'DELETE' })
            toast.success('Categoría eliminada', { description: toDelete.name })
            setDeleteOpen(false)
            await refresh()
        }
        catch (deleteError) {
            toast.error(getErrorMessage(deleteError))
        }
        finally {
            setDeleting(false)
        }
    }

    return (
        <div className='flex flex-col'>
            <form onSubmit={handleCreate} noValidate className='flex flex-col gap-3 border-b p-4 sm:p-5'>
                <label htmlFor='new-category-name' className='text-[0.8rem] font-medium'>
                    Nueva categoría
                </label>

                <div className='flex flex-col gap-2 sm:flex-row'>
                    <Input
                        id='new-category-name'
                        value={name}
                        maxLength={LIMITS.categoryName}
                        placeholder='Ej.: Herramientas, Bebidas, Recambios'
                        autoComplete='off'
                        onChange={event => {
                            setName(event.target.value)
                            setError(null)
                        }}
                        aria-invalid={Boolean(error) || undefined}
                        aria-describedby={error ? 'new-category-error' : undefined}
                        className='sm:max-w-sm'
                    />

                    <Button type='submit' disabled={creating}>
                        {creating ? <LoaderCircle className='animate-spin' aria-hidden='true' /> : <Plus aria-hidden='true' />}
                        Añadir categoría
                    </Button>
                </div>

                {error && (
                    <p id='new-category-error' className='text-xs text-destructive'>
                        {error}
                    </p>
                )}

                <ColorSwatches value={color} onChange={setColor} label='Color de la nueva categoría' />
            </form>

            {categories.length ? (
                <ul className='divide-y'>
                    {categories.map(category => (
                        <CategoryRow
                            key={`${category.id}-${category.name}-${category.color}`}
                            category={category}
                            onDelete={target => {
                                setToDelete(target)
                                setDeleteOpen(true)
                            }}
                        />
                    ))}
                </ul>
            ) : (
                <EmptyState
                    icon={Tags}
                    title='Sin categorías todavía'
                    description='Agrupa tus productos por familias para filtrarlos más rápido y ver cuánto vale cada grupo.'
                    className='py-10'
                />
            )}

            {uncategorizedCount > 0 && (
                <p className={cn('border-t px-4 py-3 text-xs text-muted-foreground sm:px-5')}>
                    <Link
                        href={{ pathname: '/inventario', query: { categoria: 'sin-categoria' } }}
                        className='font-medium text-foreground hover:underline underline-offset-4'
                    >
                        {number(uncategorizedCount)} {pluralize(uncategorizedCount, 'producto', 'productos')}
                    </Link>
                    {' '}
                    {pluralize(uncategorizedCount, 'no tiene', 'no tienen')} categoría.
                </p>
            )}

            <AlertDialog
                open={deleteOpen}
                onOpenChange={open => {
                    if (!deleting) setDeleteOpen(open)
                }}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogMedia className='bg-danger-soft text-danger-ink'>
                            <Trash2 aria-hidden='true' />
                        </AlertDialogMedia>

                        <AlertDialogTitle>
                            ¿Eliminar esta categoría?
                        </AlertDialogTitle>

                        <AlertDialogDescription>
                            {toDelete && toDelete.productCount > 0
                                ? `Sus ${number(toDelete.productCount)} ${pluralize(toDelete.productCount, 'producto quedará', 'productos quedarán')} sin categoría. No se borra ningún producto ni su stock.`
                                : 'No tiene productos asignados, así que no afecta a tu inventario.'}
                        </AlertDialogDescription>
                    </AlertDialogHeader>

                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>
                            Cancelar
                        </AlertDialogCancel>

                        <AlertDialogAction
                            onClick={handleDelete}
                            disabled={deleting}
                            className='bg-destructive text-white hover:bg-destructive/90'
                        >
                            {deleting && <LoaderCircle className='animate-spin' aria-hidden='true' />}
                            {deleting ? 'Eliminando…' : 'Eliminar categoría'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}

export default CategoryManager
